import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { slaOverzichtNavOp } from "@/lib/overzichtNav";
import { fmtEUR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Megaphone, Rocket, Trash2, Plus, Package } from "lucide-react";
import { toast } from "sonner";
import { VandaagPaneel, FlowCampagnesTabel } from "@/components/campagne/FlowOverzicht";

import { ProductLijst, FASES } from "@/components/campagne/ProductLijst";

function Overzicht({ onPickLaunch, reload }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/campagnes").then(r => setItems(r.data.filter(c => c.type !== "flow"))); }, [reload]);
  const openCamp = (cid) => {
    slaOverzichtNavOp("campagnes", items.map(c => c.id), window.location.pathname + window.location.search);
    navigate(`/campagnes/${cid}`);
  };
  return (
    <div className="bg-card border border-border rounded-xl divide-y divide-border" data-testid="campagne-overzicht">
      {items.map(c => (
        <div key={c.id}
             onClick={()=>openCamp(c.id)}
             className="p-4 flex items-center justify-between gap-4 hover:bg-secondary/40 cursor-pointer transition-colors"
             data-testid={`camp-${c.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Megaphone className="h-4 w-4 text-primary" />
              <span className="font-medium">{c.naam}</span>
              <Badge variant="outline">{c.fase}</Badge>
              {(c.product_lijst || []).length > 0 && (
                <Badge variant="secondary"><Package className="h-3 w-3 mr-1" />{c.product_lijst.length} artikel(en)</Badge>
              )}
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">{c.omschrijving || "—"}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {c.aantal_kansen || 0} kans(en) · {fmtEUR(c.totaal_bedrag || 0)} pipeline
            </div>
          </div>
          <Button size="sm" variant="outline"
                  onClick={(e)=>{ e.stopPropagation(); onPickLaunch(c); }}
                  data-testid={`launch-${c.id}`}>
            <Rocket className="h-3 w-3 mr-1" />Lanceer
          </Button>
        </div>
      ))}
      {items.length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">Nog geen campagnes. Maak er een aan via &quot;Nieuwe campagne&quot;.</div>}
    </div>
  );
}

function Creator({ onCreated }) {
  const empty = { naam: "", omschrijving: "", fase: "Cold", template_naam: "", template_bedrag: 0, product_lijst: [] };
  const [f, setF] = useState(empty);

  const submit = async () => {
    if (!f.naam.trim()) { toast.error("Naam verplicht"); return; }
    try {
      const payload = { ...f,
        template_bedrag: parseFloat(String(f.template_bedrag).replace(",", ".")) || 0,
        product_lijst: f.product_lijst.map(v => ({ product_id: v.product_id, aantal: Number(v.aantal) || 1 })),
      };
      const { data } = await api.post("/campagnes", payload);
      toast.success("Campagne aangemaakt"); setF(empty); onCreated(data);
    } catch { toast.error("Aanmaken mislukt"); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-5 max-w-3xl" data-testid="campagne-creator">
      <h2 className="font-display text-2xl font-medium">Nieuwe campagne</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Campagne-naam</Label><Input data-testid="camp-naam" value={f.naam} onChange={(e)=>setF({...f,naam:e.target.value})} placeholder="Bijv. Q3 Upsell Logistiek" /></div>
        <div><Label>Template kans-naam</Label><Input data-testid="camp-template-naam" value={f.template_naam} onChange={(e)=>setF({...f,template_naam:e.target.value})} placeholder="Q3 Pakket aanbod" /></div>
      </div>
      <div><Label>Omschrijving</Label><Textarea value={f.omschrijving} onChange={(e)=>setF({...f,omschrijving:e.target.value})} /></div>
      <div>
        <Label>Begin-fase</Label>
        <Select value={f.fase} onValueChange={(v)=>setF({...f,fase:v})}>
          <SelectTrigger data-testid="camp-fase" className="max-w-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{FASES.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="pt-2 border-t border-border">
        <ProductLijst value={f.product_lijst} onChange={(v)=>setF({...f, product_lijst: v})} />
      </div>

      {f.product_lijst.length === 0 && (
        <div className="pt-2 border-t border-border">
          <Label>Fallback bedrag (€) per kans</Label>
          <Input type="number" step="0.01" className="max-w-xs"
            value={f.template_bedrag}
            onChange={(e)=>setF({...f,template_bedrag:e.target.value})}
            data-testid="camp-template-bedrag" />
          <div className="text-xs text-muted-foreground mt-1">
            Wordt gebruikt als er geen artikelen zijn gekoppeld.
          </div>
        </div>
      )}

      <Button onClick={submit} data-testid="camp-submit">Campagne aanmaken</Button>
    </div>
  );
}

function LaunchPanel({ campagne, onClose, onLaunched }) {
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [klantgroep, setKlantgroep] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [producten, setProducten] = useState([]);

  useEffect(() => {
    api.get("/organisaties").then(r => setOrgs(r.data));
    api.get("/producten").then(r => setProducten(r.data));
  }, []);

  const prodMap = useMemo(() => Object.fromEntries(producten.map(p => [p.id, p])), [producten]);
  const totaalPerKans = (campagne.product_lijst || []).reduce((s, v) => {
    const p = prodMap[v.product_id]; return s + (Number(v.aantal)||0)*(Number(p?.verkoopprijs)||0);
  }, 0) || Number(campagne.template_bedrag || 0);

  const filtered = orgs.filter(o =>
    (!q || o.naam.toLowerCase().includes(q.toLowerCase()))
    && (!klantgroep || (o.klantgroep || "") === klantgroep)
  );
  const groepen = Array.from(new Set(orgs.map(o => o.klantgroep).filter(Boolean)));
  const allChecked = filtered.length > 0 && filtered.every(o => picked.has(o.id));
  const toggleAll = () => {
    const ns = new Set(picked);
    if (allChecked) filtered.forEach(o => ns.delete(o.id));
    else filtered.forEach(o => ns.add(o.id));
    setPicked(ns);
  };
  const toggleOne = (id) => {
    const ns = new Set(picked);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    setPicked(ns);
  };
  const launch = async () => {
    if (picked.size === 0) { toast.error("Kies minstens één relatie"); return; }
    const { data } = await api.post(`/campagnes/${campagne.id}/launch`, { relatie_ids: Array.from(picked) });
    toast.success(`${data.aangemaakt} verkoopkans(en) aangemaakt (${data.regels_per_kans} regel(s) per kans)`);
    onLaunched(); onClose();
  };

  return (
    <div className="bg-card border-2 border-primary rounded-xl p-6 space-y-4" data-testid="launch-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display text-2xl">Lanceer: {campagne.naam}</h3>
        <Button variant="ghost" onClick={onClose}>Sluiten</Button>
      </div>
      <div className="bg-secondary/50 rounded-md p-3 space-y-1">
        <div className="text-xs uppercase text-muted-foreground tracking-wide">Wat krijgt elke kans</div>
        {(campagne.product_lijst || []).length > 0 ? (
          <>
            <div className="text-sm">{campagne.product_lijst.length} artikel(en) automatisch toegevoegd</div>
            <ul className="text-xs text-muted-foreground space-y-0.5 pl-3">
              {campagne.product_lijst.map(v => {
                const p = prodMap[v.product_id]; if (!p) return null;
                return <li key={v.product_id}>· {v.aantal} × {p.naam} = {fmtEUR((v.aantal||0)*(p.verkoopprijs||0))}</li>;
              })}
            </ul>
          </>
        ) : (
          <div className="text-sm">Geen artikelen — alleen bedrag {fmtEUR(campagne.template_bedrag)} per kans</div>
        )}
        <div className="text-sm pt-1 border-t border-border/50 mt-1"><strong>Totaal per kans:</strong> {fmtEUR(totaalPerKans)}</div>
      </div>
      <div className="flex gap-3">
        <Input placeholder="Zoek relatie…" value={q} onChange={(e)=>setQ(e.target.value)} className="flex-1" />
        <select className="border border-border rounded-md px-3 bg-background" value={klantgroep} onChange={(e)=>setKlantgroep(e.target.value)}>
          <option value="">— alle klantgroepen —</option>
          {groepen.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>
      <div className="border border-border rounded-lg max-h-96 overflow-y-auto">
        <div className="sticky top-0 bg-secondary px-3 py-2 flex items-center gap-2 border-b border-border">
          <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all" />
          <span className="text-sm">{picked.size} van {filtered.length} geselecteerd</span>
        </div>
        {filtered.map(o => (
          <label key={o.id} className="flex items-center gap-3 px-3 py-2 hover:bg-secondary/50 cursor-pointer border-b border-border/50" data-testid={`pick-${o.id}`}>
            <Checkbox checked={picked.has(o.id)} onCheckedChange={() => toggleOne(o.id)} />
            <div className="flex-1"><div className="text-sm font-medium">{o.naam}</div><div className="text-xs text-muted-foreground">{o.klantgroep} · {o.plaats}</div></div>
          </label>
        ))}
      </div>
      <Button size="lg" onClick={launch} data-testid="launch-submit">
        <Rocket className="h-4 w-4 mr-2" />
        Start campagne ({picked.size} × {fmtEUR(totaalPerKans)} = {fmtEUR(picked.size * totaalPerKans)})
      </Button>
    </div>
  );
}

export default function Campagnes() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overzicht";
  const [launchFor, setLaunchFor] = useState(null);
  const [reload, setReload] = useState(0);
  return (
    <div className="space-y-6" data-testid="campagnes-page">
      <div>
        <div className="text-sm text-muted-foreground">Marketing</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Campagnes</h1>
      </div>
      <div className="flex gap-2 border-b border-border">
        {[["overzicht","Overzicht"],["nieuw","Nieuwe campagne"]].map(([k,l]) => (
          <button key={k} onClick={() => setParams({tab:k})} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab===k?"border-primary text-primary":"border-transparent text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>
      {launchFor && <LaunchPanel campagne={launchFor} onClose={() => setLaunchFor(null)} onLaunched={() => setReload(r => r + 1)} />}
      {tab === "overzicht" && (<>
        <VandaagPaneel />
        <FlowCampagnesTabel />
        <h2 className="text-base md:text-lg font-semibold pt-2">Enkelvoudige campagnes (verkoopkansen)</h2>
        <Overzicht reload={reload} onPickLaunch={(c) => setLaunchFor(c)} />
      </>)}
      {tab === "nieuw" && <Creator onCreated={() => { setReload(r => r + 1); setParams({tab:"overzicht"}); }} />}
    </div>
  );
}
